import authService from './AuthService';
import { getTimezoneHeaders, getAccountTimezone, rememberProtectionContext, getClientTimezone, getAccountToday, getProtectionRevision } from './StreakProtection';

/**
 * CalendarService
 * 
 * Manages calendar data and statistics for practice sessions.
 * Fetches daily and monthly practice statistics from the backend.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function getCalendarHeaders() {
  const token = authService.getAuthToken?.();
  const userId = authService.getCurrentUserId();
  return { ...getTimezoneHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : userId ? { 'x-user-id': userId } : {}) };
}

class CalendarService {
  constructor() {
    // Simple in-memory cache for monthly data
    this.cache = new Map();
    this.protectionRevision = getProtectionRevision();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get daily statistics for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} timezone - User's timezone (optional, defaults to browser timezone)
   * @returns {Promise<Object>} Daily stats with totalSeconds, totalRepeatCount, sessionCount
   * @throws {Error} Network or API errors
   */
  async getDailyStats(date, timezone = null) {
    const tz = getAccountTimezone() || timezone || getClientTimezone() || 'UTC';
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/practice/daily?date=${encodeURIComponent(date)}&timezone=${encodeURIComponent(tz)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getCalendarHeaders(),
          },
        }
      );

      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        const error = new Error(
          data?.error?.message || data?.message || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return rememberProtectionContext(data);
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('NetworkError')
      )) {
        const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Get monthly statistics (all days in month)
   * @param {number} year - Year number
   * @param {number} month - Month number (1-12)
   * @param {string} timezone - User's timezone (optional, defaults to browser timezone)
   * @returns {Promise<Object>} Monthly data with year, month, dailyStats array, and summary
   * @throws {Error} Network or API errors
   */
  async getMonthlyStats(year, month, timezone = null, { force = false } = {}) {
    const tz = getAccountTimezone() || timezone || getClientTimezone() || 'UTC';
    
    if (this.protectionRevision !== getProtectionRevision()) {
      this.cache.clear();
      this.protectionRevision = getProtectionRevision();
    }
    // Account and day are part of the key: a cached calendar must not cross sign-in or midnight.
    const cacheKey = `${year}-${month}-${tz}-${authService.getCurrentUserId() || ''}-${getAccountToday(tz)}`;
    const cached = this.cache.get(cacheKey);
    if (!force && cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/practice/monthly?year=${year}&month=${month}&timezone=${encodeURIComponent(tz)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getCalendarHeaders(),
          },
        }
      );

      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        const error = new Error(
          data?.error?.message || data?.message || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.data = data;
        throw error;
      }

      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return rememberProtectionContext(data);
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('NetworkError')
      )) {
        const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Get monthly summary (aggregated stats)
   * @param {number} year - Year number
   * @param {number} month - Month number (1-12)
   * @param {string} timezone - User's timezone (optional, defaults to browser timezone)
   * @returns {Promise<Object>} Monthly summary with practicedDays, totalSeconds, totalRepeatCount
   * @throws {Error} Network or API errors
   */
  async getMonthlySummary(year, month, timezone = null) {
    // Get monthly stats which includes the summary
    const monthlyData = await this.getMonthlyStats(year, month, timezone);
    return monthlyData.summary;
  }

  /**
   * Get statistics for a custom date range
   * @param {string} beginDate - Begin date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {string} timezone - User's timezone (optional, defaults to browser timezone)
   * @returns {Promise<Object>} Date range statistics with dailyStats and summary
   * @throws {Error} Network or API errors
   */
  async getDateRangeStats(beginDate, endDate, timezone = null) {
    const tz = getAccountTimezone() || timezone || getClientTimezone() || 'UTC';
    
    // Validate date format (basic check)
    const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateFormatRegex.test(beginDate)) {
      throw new Error('Invalid begin date format. Expected YYYY-MM-DD.');
    }
    if (!dateFormatRegex.test(endDate)) {
      throw new Error('Invalid end date format. Expected YYYY-MM-DD.');
    }
    
    // Validate date range
    const begin = new Date(beginDate);
    const end = new Date(endDate);
    if (end < begin) {
      throw new Error('End date cannot be before begin date.');
    }
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/practice/date-range?beginDate=${encodeURIComponent(beginDate)}&endDate=${encodeURIComponent(endDate)}&timezone=${encodeURIComponent(tz)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getCalendarHeaders(),
          },
        }
      );

      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        const error = new Error(
          data?.error?.message || data?.message || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return rememberProtectionContext(data);
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('NetworkError')
      )) {
        const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Get practice statistics for an exact date-time range.
   * Used by fixed-window challenges so progress stops at the challenge end.
   * @param {string} beginTime - ISO timestamp
   * @param {string} endTime - ISO timestamp
   * @returns {Promise<Object>} Date-time range statistics with summary totals
   */
  async getDateTimeRangeStats(beginTime, endTime) {
    const begin = new Date(beginTime);
    const end = new Date(endTime);

    if (isNaN(begin.getTime())) {
      throw new Error('Invalid begin time format. Expected ISO timestamp.');
    }
    if (isNaN(end.getTime())) {
      throw new Error('Invalid end time format. Expected ISO timestamp.');
    }
    if (end < begin) {
      throw new Error('End time cannot be before begin time.');
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/practice/date-time-range?beginTime=${encodeURIComponent(begin.toISOString())}&endTime=${encodeURIComponent(end.toISOString())}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getCalendarHeaders(),
          },
        }
      );

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        const error = new Error(
          data?.error?.message || data?.message || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return rememberProtectionContext(data);
    } catch (error) {
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('NetworkError')
      )) {
        const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      throw error;
    }
  }

  /**
   * Get grade distribution for all flashcards
   * @returns {Promise<Object>} Grade distribution with counts for each grade level
   * @throws {Error} Network or API errors
   */
  async getGradeDistribution() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/flashcards/grade-distribution`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getCalendarHeaders(),
          },
        }
      );

      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        const error = new Error(
          data?.error?.message || data?.message || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return rememberProtectionContext(data);
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('NetworkError')
      )) {
        const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Get repeat count distribution for all flashcards
   * @returns {Promise<Object>} Repeat count distribution with counts for each range
   * @throws {Error} Network or API errors
   */
  async getRepeatDistribution() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/flashcards/repeat-distribution`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getCalendarHeaders(),
          },
        }
      );

      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        const error = new Error(
          data?.error?.message || data?.message || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return rememberProtectionContext(data);
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('NetworkError')
      )) {
        const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Clear the cache (useful for testing or forcing refresh)
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Invalidate cache for a specific month
   * @param {number} year - Year number
   * @param {number} month - Month number (1-12)
   * @param {string} timezone - User's timezone (optional)
   */
  invalidateMonth(year, month, timezone = null) {
    const tz = getAccountTimezone() || timezone || getClientTimezone() || 'UTC';
    const prefix = `${year}-${month}-${tz}-`;
    for (const key of this.cache.keys()) if (key.startsWith(prefix)) this.cache.delete(key);
  }
}

export default CalendarService;
